/**
 * PolicyBulkImportPage — bulk PDF policy importer with AI metadata extraction.
 * CLARID-SPEC-MIG-002.
 *
 * Single page handles upload → background extraction (polled) → review → commit.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { policyApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';

const CATEGORIES = ['LENDING','DEPOSITS','BSA_AML','FAIR_LENDING','MARKETING','PRIVACY','VENDOR_MANAGEMENT','IT_SECURITY','OPERATIONS','HR','BOARD','OTHER'] as const;

interface ImportItem {
  itemId: string;
  sourceFileName: string;
  status: 'EXTRACTING' | 'EXTRACTED' | 'CONFIRMED' | 'REJECTED' | 'FAILED' | 'COMMITTED';
  extracted?: {
    title: string | null;
    category: string | null;
    effectiveDate: string | null;
    regulatoryReferences: string[];
    ownerHint: string | null;
    confidence: {
      title: number; category: number; effectiveDate: number; regulatoryReferences: number; overall: number;
    };
    reasoning: string;
  };
  errorMessage?: string;
  overrides?: any;
}

interface Preview {
  previewId: string;
  totalFiles: number;
  items: ImportItem[];
}

export default function PolicyBulkImportPage() {
  const { teamId } = useTeamId();
  const navigate = useNavigate();
  const [files, setFiles] = useState<File[]>([]);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [defaultDept, setDefaultDept] = useState('');
  const [defaultReviewDays, setDefaultReviewDays] = useState(365);

  const uploadMutation = useMutation({
    mutationFn: async () => policyApi.bulkImport.upload(teamId!, files),
    onSuccess: (res) => {
      setPreviewId(res.data.previewId);
      toast.success(`${res.data.preview.totalFiles} files uploaded — AI extraction running`);
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Upload failed'),
  });

  // Poll preview while extracting
  const { data: previewData } = useQuery({
    queryKey: ['policy-bulk-import-preview', teamId, previewId],
    queryFn: async () => {
      const res = await policyApi.bulkImport.getPreview(teamId!, previewId!);
      return res.data.preview as Preview;
    },
    enabled: !!previewId,
    refetchInterval: (q) => {
      const data = q.state.data as Preview | undefined;
      if (!data) return 3000;
      const stillProcessing = data.items.some(i => i.status === 'EXTRACTING');
      return stillProcessing ? 3000 : false;
    },
  });

  const patchItem = useMutation({
    mutationFn: async ({ itemId, data }: { itemId: string; data: any }) =>
      policyApi.bulkImport.patchItem(teamId!, previewId!, itemId, data),
  });

  const commitMutation = useMutation({
    mutationFn: async () => policyApi.bulkImport.commit(teamId!, previewId!, {
      defaultOwner,
      defaultStatus: 'DRAFT',
      defaultDepartment: defaultDept || undefined,
      defaultReviewFrequencyDays: defaultReviewDays,
    }),
    onSuccess: (res) => {
      toast.success(`${res.data.committedCount} policies imported as DRAFT`);
      navigate('/policies');
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Commit failed'),
  });

  // BE expects an explicit userId. We try the current user's id off /me — simplest is to require
  // the user to confirm and the API server resolves the requester's userId as defaultOwner.
  // The route requires defaultOwner as a string; we send the user's email-equivalent string they
  // can override via the textarea below if desired.
  const [defaultOwner, setDefaultOwner] = useState('');
  useEffect(() => {
    // Best-effort: try to read from auth state; otherwise blank and user fills it in.
    try {
      const raw = localStorage.getItem('user') || sessionStorage.getItem('user') || '';
      if (raw) {
        const u = JSON.parse(raw);
        if (u?.id) setDefaultOwner(u.id);
      }
    } catch { /* noop */ }
  }, []);

  const items = previewData?.items ?? [];
  const stillProcessing = items.some(i => i.status === 'EXTRACTING');
  const extracted = items.filter(i => i.status === 'EXTRACTED' || i.status === 'CONFIRMED');
  const failed = items.filter(i => i.status === 'FAILED');
  const rejected = items.filter(i => i.status === 'REJECTED');
  const committable = extracted.length;

  const handleCommit = () => {
    if (!defaultOwner) {
      toast.error('Default owner (your user ID) is required');
      return;
    }
    commitMutation.mutate();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <button onClick={() => navigate('/policies')} className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4">
        <ArrowLeftIcon className="h-4 w-4 mr-1" /> Back to Policies
      </button>

      <h1 className="text-2xl font-bold text-navy-900">Bulk import policies</h1>
      <p className="mt-1 text-sm text-gray-500">
        Upload up to 50 PDF policy documents. Clarid extracts metadata (title, category, effective
        date, regulatory references) via AI, then you review and confirm. Imported policies land as
        DRAFT and route through your standard review queue.
      </p>

      {!previewId && (
        <div className="mt-6 bg-white rounded-lg shadow p-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Upload PDFs (up to 50)</label>
          <input
            type="file"
            multiple
            accept=".pdf,application/pdf"
            onChange={(e) => setFiles(Array.from(e.target.files ?? []).slice(0, 50))}
            className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-ocean-50 file:text-ocean-700 hover:file:bg-ocean-100"
          />
          {files.length > 0 && (
            <p className="mt-2 text-sm text-gray-600">{files.length} file{files.length === 1 ? '' : 's'} selected</p>
          )}
          <button
            onClick={() => uploadMutation.mutate()}
            disabled={files.length === 0 || uploadMutation.isPending}
            className="mt-4 inline-flex items-center px-4 py-2 bg-navy-900 text-white text-sm font-medium rounded-md hover:bg-navy-800 disabled:opacity-50"
          >
            <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
            {uploadMutation.isPending ? 'Uploading…' : `Upload and extract ${files.length} files`}
          </button>
        </div>
      )}

      {previewData && (
        <div className="mt-6 space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {stillProcessing ? 'Extracting…' : 'Extraction complete'}
                </div>
                <div className="mt-1 text-xl font-bold text-navy-900">
                  {extracted.length} of {previewData.totalFiles} extracted
                  {failed.length > 0 && ` · ${failed.length} failed`}
                </div>
              </div>
              {stillProcessing && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <svg className="animate-spin h-4 w-4 text-ocean-600" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" opacity="0.25" />
                    <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" opacity="0.75" />
                  </svg>
                  Polling…
                </div>
              )}
            </div>
          </div>

          {/* Extracted items table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-navy-900">Extracted metadata</h3>
            </div>
            <div className="overflow-x-auto max-h-[600px]">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase">File</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase">Title</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase">Category</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase">Effective</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase">Confidence</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {items.map(item => (
                    <ImportItemRow
                      key={item.itemId}
                      item={item}
                      onUpdate={(data) => patchItem.mutate({ itemId: item.itemId, data })}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Commit footer */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Default owner (userId)</label>
                <input
                  value={defaultOwner}
                  onChange={(e) => setDefaultOwner(e.target.value)}
                  placeholder="Your user ID"
                  className="block w-full text-sm border border-gray-300 rounded-md px-3 py-1.5"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Default department</label>
                <input
                  value={defaultDept}
                  onChange={(e) => setDefaultDept(e.target.value)}
                  placeholder="Compliance"
                  className="block w-full text-sm border border-gray-300 rounded-md px-3 py-1.5"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Review cycle (days)</label>
                <input
                  type="number"
                  value={defaultReviewDays}
                  onChange={(e) => setDefaultReviewDays(parseInt(e.target.value, 10) || 365)}
                  className="block w-full text-sm border border-gray-300 rounded-md px-3 py-1.5"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              All imported policies land as <strong>DRAFT</strong> and route through your standard review queue.
              {rejected.length > 0 && ` ${rejected.length} rejected items will be skipped.`}
            </p>
            <button
              onClick={handleCommit}
              disabled={committable === 0 || stillProcessing || !defaultOwner}
              className="inline-flex items-center px-6 py-2.5 bg-navy-900 text-white text-sm font-semibold rounded-md hover:bg-navy-800 disabled:opacity-50"
            >
              Import {committable} policies as DRAFT
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ImportItemRow({ item, onUpdate }: { item: ImportItem; onUpdate: (data: any) => void }) {
  const e = item.extracted;
  const o = item.overrides ?? {};
  const titleVal = o.title ?? e?.title ?? '';
  const categoryVal = o.category ?? e?.category ?? '';
  const dateVal = o.effectiveDate ?? e?.effectiveDate ?? '';

  if (item.status === 'EXTRACTING') {
    return (
      <tr>
        <td className="px-3 py-2 text-gray-500">{item.sourceFileName}</td>
        <td colSpan={5} className="px-3 py-2 text-xs text-gray-400 italic">
          <ClockIcon className="h-4 w-4 inline mr-1" /> Extracting…
        </td>
      </tr>
    );
  }
  if (item.status === 'FAILED') {
    return (
      <tr className="bg-red-50">
        <td className="px-3 py-2">{item.sourceFileName}</td>
        <td colSpan={5} className="px-3 py-2 text-xs text-red-700">
          <XCircleIcon className="h-4 w-4 inline mr-1" /> {item.errorMessage || 'Extraction failed'}
        </td>
      </tr>
    );
  }
  if (item.status === 'REJECTED') {
    return (
      <tr className="bg-gray-50 opacity-60">
        <td className="px-3 py-2">{item.sourceFileName}</td>
        <td colSpan={4} className="px-3 py-2 text-xs text-gray-500 italic">Rejected</td>
        <td className="px-3 py-2"><button onClick={() => onUpdate({ status: 'EXTRACTED' })} className="text-ocean-600 text-xs">Include</button></td>
      </tr>
    );
  }

  const conf = e?.confidence.overall ?? 0;
  const confColor = conf >= 0.8 ? 'text-green-600' : conf >= 0.4 ? 'text-amber-600' : 'text-red-600';

  return (
    <tr>
      <td className="px-3 py-2 text-gray-600">{item.sourceFileName}</td>
      <td className="px-3 py-2">
        <input
          value={titleVal}
          onChange={(ev) => onUpdate({ overrides: { title: ev.target.value } })}
          className="text-xs border-0 border-b border-transparent hover:border-gray-300 focus:border-ocean-500 focus:outline-none w-full"
        />
      </td>
      <td className="px-3 py-2">
        <select
          value={categoryVal || ''}
          onChange={(ev) => onUpdate({ overrides: { category: ev.target.value } })}
          className="text-xs border border-gray-200 rounded px-1 py-0.5"
        >
          <option value="">—</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </td>
      <td className="px-3 py-2">
        <input
          type="date"
          value={dateVal || ''}
          onChange={(ev) => onUpdate({ overrides: { effectiveDate: ev.target.value } })}
          className="text-xs border-0"
        />
      </td>
      <td className={`px-3 py-2 text-xs font-medium ${confColor}`}>
        {(conf * 100).toFixed(0)}%
        {conf >= 0.8 && <CheckCircleIcon className="h-4 w-4 inline ml-1" />}
        {conf >= 0.4 && conf < 0.8 && <ExclamationTriangleIcon className="h-4 w-4 inline ml-1" />}
      </td>
      <td className="px-3 py-2">
        <button onClick={() => onUpdate({ status: 'REJECTED' })} className="text-xs text-red-600 hover:text-red-800">
          Reject
        </button>
      </td>
    </tr>
  );
}
